import { FilterCriteria, RangeFilter } from '../../FilterStore'
import { FilterChipCloseButton, FilterChipContainer } from './FilterChipStyles'
import { useTableLinkVariant } from 'components/Tables/CommonTableCells/TableLinkStyleContext'

type FilterChipRangeProps = {
  title?: string
  value: RangeFilter
  onClose?: () => void
  filterCriteria: FilterCriteria
  isHovered?: boolean
  /** Currency / unit prefix (e.g. "$"). Looked up from the store's per-key
   *  `filterPrefixes` registry and stitched onto each numeric leg so a
   *  money chip reads "Lifetime Value: $15K – $80K". Date-typed values
   *  skip the prefix since "T2026-05-28" makes no sense. */
  prefix?: string
  /** Decimal precision from the store's `filterDecimalScales` registry —
   *  preserves the cents on non-round currency values so the chip
   *  shows "$50,500.50" instead of toLocaleString's "$50,500.5". */
  decimalScale?: number
}

export function FilterChipRange({
  title,
  value,
  onClose,
  filterCriteria,
  isHovered,
  prefix,
  decimalScale = 0,
}: FilterChipRangeProps) {
  const { max, min } = value
  const isExclusive = filterCriteria === 'neither'
  const variant = useTableLinkVariant()
  const isPlain = variant === 'plain'

  const formatStrAsLocaleDate = (str: string) => {
    const date = new Date(str + 'T00:00:00')
    return date.toLocaleDateString()
  }

  // Abbreviate only when the number is an exact multiple of 1K / 1M / 1B —
  // a "ballpark" value where trailing zeros carry no information. Specific
  // numbers (e.g. 12,345) keep all digits so the user sees what they typed.
  // When a prefix is set (e.g. "$"), stitch it onto the abbreviated body.
  // decimalScale forces the locale fallback to preserve precision so a
  // stored 50500.50 renders as "$50,500.50" instead of "$50,500.5".
  const formatChipNumber = (n: number) => {
    let body: string
    if (n === 0) {
      body = '0'
    } else {
      const abs = Math.abs(n)
      if (abs >= 1_000_000_000 && n % 1_000_000_000 === 0) {
        body = `${n / 1_000_000_000}B`
      } else if (abs >= 1_000_000 && n % 1_000_000 === 0) {
        body = `${n / 1_000_000}M`
      } else if (abs >= 1_000 && n % 1_000 === 0) {
        body = `${n / 1_000}K`
      } else {
        body = n.toLocaleString(undefined, {
          minimumFractionDigits: decimalScale,
          maximumFractionDigits: decimalScale,
        })
      }
    }
    return prefix ? `${prefix}${body}` : body
  }

  const formatValue = (value: number | string) => {
    if (typeof value === 'string') {
      const has3hyphens = value.split('-').length === 3
      if (has3hyphens) return formatStrAsLocaleDate(value)
      const parsed = Number(value)
      if (!Number.isNaN(parsed) && value.trim() !== '')
        return formatChipNumber(parsed)
      return value
    }
    return formatChipNumber(value)
  }

  const getFormattedValue = () => {
    if (min == null && max != null) return `≤ ${formatValue(max)}`
    if (max == null && min != null) return `≥ ${formatValue(min)}`

    return `${formatValue(min!)} - ${formatValue(max!)}`
  }

  return (
    <FilterChipContainer
      $isExclusive={isPlain ? false : isExclusive}
      isHovered={isHovered}
    >
      <span>
        {`${title}: `}
        {isPlain && isExclusive ? <b>NOT </b> : null}
        <b>{getFormattedValue()}</b>
      </span>
      {onClose && (
        <FilterChipCloseButton
          $isExclusive={isPlain ? false : isExclusive}
          onClick={onClose}
        />
      )}
    </FilterChipContainer>
  )
}
